import React, { useState } from "react";
import { HelpCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import { CARE_PACKAGES } from "../lib/data";
import CTASection from "@/components/CTASection";
import { Link} from "react-router-dom";

function FeatureCell({ label, helpText, help }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="group relative inline-flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
      {help && (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label={`More about ${label}`}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      )}

      {help && (
        <div
          className={`absolute left-0 top-full z-20 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-600 shadow-lg transition-all dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 ${
            isOpen ? "visible opacity-100" : "invisible opacity-0"
          } group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100`}
        >
          {helpText}
        </div>
      )}
    </div>
  );
}

export default function PackageComparison() {
  const { heading, description, badge, columns, rows } = CARE_PACKAGES;

  return (
    <>
      <PageHero
        eyebrow="Care packages"
        title="A clear view of what’s included."
      >
        <Link to="/services" className="badge-pill mb-6 inline-flex" data-testid="service-back">
          ← All services
        </Link>
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="rounded-[28px] border border-slate-200/70 bg-white/80 p-4 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/70 sm:p-6 lg:p-8">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                {heading}
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
                {description}
              </p>
            </div>
            <span className="badge-pill bg-slate-900/5 text-slate-700 ring-slate-200/70 dark:bg-white/10 dark:text-slate-200 dark:ring-white/10">
              {badge}
            </span>
          </div>

          <div className="overflow-x-auto rounded-[24px] border border-slate-200/70 dark:border-slate-800">
            <table className="w-full min-w-[800px] divide-y divide-slate-200 dark:divide-slate-800">
              <thead className="bg-slate-50/80 dark:bg-slate-950/60">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                    Feature
                  </th>
                  {columns.map((column) => (
                    <th key={column} className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-white/60 dark:divide-slate-800 dark:bg-slate-900/40">
                {rows.map((row) => (
                  <tr key={row.label} className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/50">
                    <td className="px-5 py-4">
                      <FeatureCell label={row.label} helpText={row.helpText} help={row.help} />
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{row.values[0]}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{row.values[1]}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{row.values[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
